import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { Map } from 'immutable';
import { canUseDOM } from 'exenv';
import url from 'url';
import cx from 'classnames';

import Brand from 'models/brand';
import UserModel from 'models/user';

import ConnectedSignIn from 'components/common/sign-in-form/connected-sign-in';
import GoogleRecaptcha from 'components/common/recaptcha';
import InputText from 'components/common/input/text';
import InputEmail from 'components/common/input/email';
import InputPassword from 'components/common/input/password';
import ResetActiveAccountModal from 'components/receipt/reset-active-account-modal';
import SignUpInfo from 'components/account/sign-up/info';
import TermsAndConditions from 'components/common/terms-and-conditions';

import isEmailValid from 'lib/checkout/isEmailValid';
import { PAGEVIEW_EVENT } from 'lib/analytics/events/index';
import { pageProps, urlProps } from 'lib/analytics/page-properties';
import { imageCdnAsset, imageCdnAssetSet, buildRedirectURI } from 'lib/common/url-helpers';

import addErrors from 'action-creators/account/add-errors';
import cleanupSignUpState from 'action-creators/account/cleanup-sign-up-state';
import dismissActivateAccount from 'action-creators/bookings/dismiss-activate-account';
import dismissError from 'action-creators/account/dismiss-error';
import resetPassword from 'action-creators/account/reset-password';
import signUp from 'action-creators/account/sign-up';
import trackEventCreator from 'action-creators/analytics/track-event';

import BrandName from 'containers/common/brand-name';
import MarketingOptIn from 'containers/common/marketing-opt-in';



const SIGN_UP_ERRORS = {
  email: 'Email is required',
  firstName: 'First name is required',
  lastName: 'Last name is required',
  password: 'Must be at least 8 Characters',
  invalidEmail: 'Valid email required',
};

const SIGN_UP_TEXT_FIELDS = [
  { display: 'first name', value: 'firstName' },
  { display: 'last name', value: 'lastName' },
];

const propTypes = {
  brand: PropTypes.instanceOf(Brand).isRequired,
  signUpFields: PropTypes.instanceOf(Map),
  createUser: PropTypes.func.isRequired,
  dismissError: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(UserModel),
  dismissActivateAccount: PropTypes.func.isRequired,
  shouldShowActivateAccountModal: PropTypes.bool.isRequired,
  errors: PropTypes.instanceOf(Map).isRequired,
  resetPassword: PropTypes.func.isRequired,
  cleanupSignUpState: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  routerLocation: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};

const defaultProps = {
  signUpFields: Map(),
  user: null,
};

const DEFAULT_REDIRECT_URI = '/account/';

export class SignUp extends Component {
  constructor(props) {
    super(props);

    this.dismissError = this.dismissError.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleMarketingOptInChange = this.handleMarketingOptInChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    const email = get(this.props.signUpFields, 'email', '');
    const firstName = get(this.props.signUpFields, 'firstName', '');
    const lastName = get(this.props.signUpFields, 'lastName', '');

    this.state = {
      email,
      firstName,
      lastName,
      password: '',
      marketingAllowed: false,
    };

    this.captcha = React.createRef();
  }

  componentWillMount() {
    if (canUseDOM) {
      const pageProperties = pageProps({ app: 'Sign Up' });
      this.props.trackEvent({
        ...PAGEVIEW_EVENT,
        ...pageProperties,
        name: pageProperties.pageName,
        properties: urlProps(),
      });
    }
  }

  componentWillUnmount() {
    this.props.cleanupSignUpState();
  }

  dismissError(value) {
    this.props.dismissError({ error: value });
  }

  handleChange(value, e) {
    const newState = {};
    newState[value] = e.target.value;
    this.setState(newState);
  }

  handleMarketingOptInChange(marketingAllowed) {
    this.setState({ marketingAllowed });
  }

  redirectURI() {
    return buildRedirectURI(this.props.routerLocation);
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    // Validate everything is filled out
    const errors = {};
    if (!this.captcha.current || !this.captcha.current.isValid) return;

    // checks if inputs are valid
    Object.keys(this.state).forEach((field) => {
      this.dismissError(field);
      const value = this.state[field];
      if (value === '') errors[field] = SIGN_UP_ERRORS[field];
      else if (field === 'email' && !isEmailValid(value)) errors[field] = SIGN_UP_ERRORS.invalidEmail;
      else if (field === 'password' && value.length < 8) errors[field] = SIGN_UP_ERRORS[field];
    });

    if (!Object.keys(errors).length) {
      const { email, firstName, lastName, password, marketingAllowed } = this.state;
      const userData = { email, firstName, lastName, password, marketingAllowed };
      this.props.createUser({
        ...userData,
        redirectURI: this.redirectURI(),
      });
    } else {
      this.props.setErrors(errors);
    }
  }

  renderPasswordResetModal() {
    const {
      user,
      brand,
      shouldShowActivateAccountModal,
      trackEvent,
    } = this.props;
    return (
      <ResetActiveAccountModal
        shouldShowActivateAccountModal={shouldShowActivateAccountModal}
        dismissActivateAccount={this.props.dismissActivateAccount}
        resetPassword={this.props.resetPassword}
        user={user}
        brand={brand}
        trackEvent={trackEvent}
      />
    );
  }

  render() {
    const errors = this.props.errors;

    const containerClasses = cx({
      'background-blue-gradient': !this.props.brand.isBestParking,
      'background-color-bp-dark-blue': this.props.brand.isBestParking,
      'text-color-dark-slate': true,
      'force-full-height': true,
      'signup': true,
    });

    const buttonClasses = cx({
      'btn-block': true,
      'btn-primary': true,
      'text-center': true,
      'btn-bp': this.props.brand.isBestParking,
      'btn-disabled': !(this.captcha.current && this.captcha.current.isValid),
    });

    const brandNameText = this.props.brand.isBestParking ? 'BestParking' : 'ParkWhiz';

    const signInLink = { pathname: '/account/signin' };
    if (get(this.props.routerLocation, 'search', null)) {
      Object.assign(signInLink, { search: this.props.routerLocation.search });
    }

    return (
      <div className={containerClasses}>
        <div className="container margin-bottom-120">
          <div className="row display-flex">
            <div className="heavy-shadowed col-gutter-30 left-rounded-corners col-md-offset-2 col-md-3 hidden-lte-sm background-off-white">
              <img
                className="signup-car"
                src={imageCdnAsset('/images/signup/car.png')}
                srcSet={imageCdnAssetSet('/images/signup/car.png 314w, /images/signup/car.png 628w, /images/signup/car.png 942w')}
                alt="car"
              />
              <div className="signup-car-padding">
                <p className="text-weight-bold text-size-28 line-height-full">
                  Book Now
                  <br />
                  With <BrandName />.
                </p>
                <p className="text-weight-light text-size-16">We&apos;ll save a spot for ya!</p>
                <SignUpInfo info="Save up to 50% off drive-up rates." />
                <SignUpInfo info="Book your space in advance." />
                <SignUpInfo info="Get a parking pass you can access on your phone." />
              </div>
            </div>
            <div className="heavy-shadowed col-gutter-30 right-rounded-corners rounded-corners-lte-sm col-md-offset-0 col-xs-offset-1 col-md-5 col-xs-10 background-white padding-vertical-40">
              <div className="text-weight-bold text-size-xs-24 text-size-md-28">Sign Up</div>
              <form onSubmit={this.onSubmit} noValidate>
                {
                  SIGN_UP_TEXT_FIELDS.map(field => (
                    <InputText
                      key={`input-text-${field.value}`}
                      display={field.display}
                      error={errors.get(field.value)}
                      value={field.value}
                      dismissError={this.dismissError}
                      onChange={this.handleChange}
                      initialValue={this.state[field.value]}
                    />
                  ))
                }
                <InputEmail
                  key="input-text-email"
                  display="email"
                  error={errors.get('email')}
                  value="email"
                  dismissError={this.dismissError}
                  onChange={this.handleChange}
                  initialValue={this.state.email}
                />
                <InputPassword
                  display="password"
                  error={errors.get('password')}
                  value="password"
                  dismissError={this.dismissError}
                  onChange={this.handleChange}
                  showTip={false}
                />
                <div className="row">
                  <MarketingOptIn className="col-xs-12 margin-bottom-40" onChange={this.handleMarketingOptInChange} />
                </div>
                <GoogleRecaptcha ref={this.captcha} rerenderParent={this.forceUpdate.bind(this)} />
                <TermsAndConditions />
                <div className="row margin-bottom-40">
                  <div className="col-xs-8 col-xs-offset-2">
                    <button className={buttonClasses} onSubmit={this.onSubmit}>SIGN UP</button>
                  </div>
                </div>
              </form>
              <ConnectedSignIn
                marketingAllowed={this.state.marketingAllowed}
                redirectURI={this.redirectURI()}
              />
              <div className="margin-top-40 text-size-xs-12 text-size-md-14">
                ALREADY PARKING WITH US? <Link to={signInLink}>SIGN IN!</Link>
              </div>
            </div>
          </div>
        </div>
        { this.renderPasswordResetModal() }
      </div>
    );
  }
}

SignUp.propTypes = propTypes;
SignUp.defaultProps = defaultProps;

const mapStateToProps = (state, ownProps) => {
  const {
    errors,
    signUpFields,
    user,
  } = state.account;

  const {
    brand,
  } = state.brand;

  let { location: routerLocation } = state.router;
  if (!routerLocation) {
    ({ routerLocation } = ownProps);
  }

  const {
    activateAccountModalDismissed,
    shouldShowActivateAccountModal,
  } = state.bookings;

  return {
    brand,
    errors,
    signUpFields,
    routerLocation,
    user,
    activateAccountModalDismissed,
    shouldShowActivateAccountModal,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    createUser: signUp,
    dismissError,
    setErrors: addErrors,
    trackEvent: trackEventCreator,
    cleanupSignUpState,
    resetPassword,
    dismissActivateAccount,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
